import { Gauge, Loader2 } from "lucide-react";
import {
  useCanAccess,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Fit } from "../types";
import { runAiJson } from "./ai";
import {
  buildFitPrompt,
  criteriaOf,
  fitData,
  FitAnswerSchema,
  overallFit,
  reasonsFrom,
  type FitResource,
} from "./fit";

const MATCH_STYLES = {
  3: "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100",
  2: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  1: "bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-100",
};

/** "Fit 4/5" chip, coloured by match; nothing when the record was never scored */
export const FitBadge = ({ record }: { record?: Fit }) => {
  const translate = useTranslate();
  if (record?.fit_score == null || !record.fit_match) return null;
  return (
    <Badge
      className={cn("border-transparent", MATCH_STYLES[record.fit_match])}
      title={translate(`resources.fit.matches.${record.fit_match}`)}
    >
      <Gauge />
      {translate("resources.fit.badge", { score: record.fit_score })}
    </Badge>
  );
};

/** The fit score with its reasons, and a "Score fit" button for those who can edit the record */
export const FitScore = ({ resource }: { resource: FitResource }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext<Fit & Record<string, any>>();
  const { ideal_customers: profiles = [] } = useConfigurationContext();
  const { canAccess: canEdit } = useCanAccess({ resource, action: "edit" });
  const [profileName, setProfileName] = useState<string>();
  const [scoring, setScoring] = useState(false);
  const [update] = useUpdate();
  if (!record) return null;

  const profile =
    profiles.find((p) => p.name === (profileName ?? record.fit_profile)) ??
    profiles[0];

  const score = async () => {
    if (!profile) return;
    const criteria = criteriaOf(profile, resource);
    setScoring(true);
    try {
      const answer = await runAiJson(
        "score_fit",
        buildFitPrompt(profile, criteria, resource, fitData(resource, record)),
        FitAnswerSchema,
      );
      const fit_reasons = reasonsFrom(criteria, answer);
      const overall = overallFit(fit_reasons);
      if (!overall) throw new Error("No good-fit criterion could be scored");
      await update(
        resource,
        {
          id: record.id,
          data: {
            ...overall,
            fit_reasons,
            fit_profile: profile.name,
            fit_scored_at: new Date().toISOString(),
          },
          previousData: record,
        },
        { returnPromise: true },
      );
      refresh();
    } catch (error) {
      console.error("Fit scoring failed", error);
      notify("resources.fit.failed", { type: "error" });
    } finally {
      setScoring(false);
    }
  };

  const reasons = Object.entries(record.fit_reasons ?? {});
  if (!reasons.length && (!canEdit || !profiles.length)) return null;

  return (
    <div className="mt-3 rounded-md border p-3 text-sm space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{translate("resources.fit.title")}</span>
        <FitBadge record={record} />
        {record.fit_profile && (
          <span className="text-xs text-muted-foreground">
            {record.fit_profile}
          </span>
        )}
        <div className="flex-1" />
        {canEdit && profiles.length > 1 && (
          <select
            aria-label={translate("resources.fit.profile")}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            value={profile?.name}
            onChange={(e) => setProfileName(e.target.value)}
          >
            {profiles.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        {canEdit && profile && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={score}
            disabled={scoring}
          >
            {scoring ? <Loader2 className="animate-spin" /> : <Gauge />}
            {translate(
              scoring ? "resources.fit.scoring" : "resources.fit.action",
            )}
          </Button>
        )}
      </div>
      {reasons.length > 0 && (
        <ul className="space-y-1">
          {reasons.map(([criterion, reason]) => (
            <li key={criterion} className="flex gap-2">
              <span
                className={cn(
                  "w-8 shrink-0 text-right tabular-nums",
                  reason.kind === "bad" && reason.score
                    ? "text-destructive"
                    : "text-muted-foreground",
                )}
              >
                {reason.score === null ? "?" : `${reason.score}/5`}
              </span>
              <span>
                <span className="font-medium">
                  {reason.kind === "bad" ? "⚠ " : ""}
                  {criterion}
                </span>
                {reason.reasoning && (
                  <span className="text-muted-foreground">
                    {" "}
                    · {reason.reasoning}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
